'use client';

/**
 * Where does this go? — the folder chooser for Ctrl+7 / the rail button.
 *
 * A personal notebook is somewhere you file things on PURPOSE, so keeping a
 * translation asks which folder rather than dropping it somewhere by default.
 * This is a small window over the translator (not the whole notebook): it lists
 * the folders you have made, lets you make one on the spot, and files the held
 * translation the moment you choose. "Browse" is the way out for anyone who
 * reached for it only to look.
 *
 * It reads ONLY the user's own folders — `/api/notebook` returns the personal
 * tree (the folders you created), never the behind-the-scenes capture — so there
 * is nothing here that tells you anything is recorded. The last folder used is
 * pre-selected, because the work usually continues where it left off.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchNotebook, createFolder, loadLastFolder,
  type NotebookFolder,
} from '@/lib/notebook';
import Layer from '../Layer';

export default function FolderSavePicker({
  onChoose,
  onClose,
  onBrowse,
}: {
  onChoose: (folderId: string) => void;
  onClose: () => void;
  onBrowse: () => void;
}) {
  const [folders, setFolders] = useState<NotebookFolder[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const firstFolderRef = useRef<HTMLButtonElement>(null);

  // One read of the personal tree. Folders are already sorted by the server.
  useEffect(() => {
    let alive = true;
    fetchNotebook()
      .then((t) => { if (alive) setFolders(t.folders); })
      .catch((err: Error) => { if (alive) setLoadError(err.message); });
    return () => { alive = false; };
  }, []);

  // The folder most likely wanted: the one last used, if it still exists.
  const lastId = useMemo(() => {
    const last = loadLastFolder();
    return folders?.some((f) => f.id === last) ? last : null;
  }, [folders]);

  // Land focus somewhere useful: the first folder if there are any, else the
  // new-folder box (an empty notebook's only move is to make its first folder).
  useEffect(() => {
    if (!folders) return;
    const t = setTimeout(() => {
      if (folders.length) firstFolderRef.current?.focus();
      else nameRef.current?.focus();
    }, 30);
    return () => clearTimeout(t);
  }, [folders]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  async function makeAndChoose() {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const { folder } = await createFolder(name, null);
      onChoose(folder.id);
    } catch (err) {
      setCreateError((err as Error).message);
      setCreating(false);
    }
  }

  return (
    <Layer>
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Keep this translation in a folder"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden />
      <div className="relative flex w-full max-w-sm flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 dark:bg-ink-900 dark:ring-white/10">
        <div className="border-b border-ink-100 px-5 pb-3 pt-4 dark:border-ink-800">
          <h2 className="text-[15px] font-semibold text-ink-900 dark:text-ink-50">Keep in your notebook</h2>
          <p className="mt-0.5 text-[12px] text-ink-500 dark:text-ink-400">Choose a folder for this translation.</p>
        </div>

        <div className="max-h-[46vh] overflow-y-auto px-3 py-3">
          {loadError ? (
            <p className="px-2 py-6 text-center text-[13px] text-red-600 dark:text-red-300">{loadError}</p>
          ) : !folders ? (
            <p className="px-2 py-6 text-center text-[13px] text-ink-400">Loading your folders…</p>
          ) : folders.length === 0 ? (
            <p className="px-2 py-4 text-center text-[13px] text-ink-500 dark:text-ink-400">
              No folders yet — name one below to keep this in it.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {folders.map((f, i) => (
                <li key={f.id}>
                  <button
                    ref={i === 0 ? firstFolderRef : undefined}
                    type="button"
                    onClick={() => onChoose(f.id)}
                    className={
                      'group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] transition ' +
                      'hover:bg-accent-50 focus:bg-accent-50 focus:outline-none dark:hover:bg-accent-500/10 dark:focus:bg-accent-500/10 ' +
                      (f.id === lastId ? 'ring-1 ring-accent-300 dark:ring-accent-500/40' : '')
                    }
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-ink-400 group-hover:text-accent-500" fill="currentColor" aria-hidden>
                      <path d="M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    </svg>
                    <span className="min-w-0 flex-1 truncate font-medium text-ink-800 dark:text-ink-100">{f.name}</span>
                    {f.id === lastId && <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-accent-500">Last</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-ink-100 px-3 py-3 dark:border-ink-800">
          <div className="flex items-center gap-2">
            <input
              ref={nameRef}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void makeAndChoose(); } }}
              placeholder="New folder name…"
              maxLength={80}
              className="min-w-0 flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 text-[13px] text-ink-900 placeholder:text-ink-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-200 dark:border-ink-700 dark:bg-ink-950 dark:text-ink-50 dark:focus:ring-accent-500/30"
            />
            <button
              type="button"
              onClick={() => void makeAndChoose()}
              disabled={!newName.trim() || creating}
              className="shrink-0 rounded-lg bg-accent-600 px-3 py-2 text-[13px] font-semibold text-white transition hover:bg-accent-700 disabled:opacity-40 dark:bg-accent-400 dark:text-ink-900 dark:hover:bg-accent-300"
            >
              {creating ? 'Saving…' : 'Create & save'}
            </button>
          </div>
          {createError && <p className="mt-2 text-[12px] text-red-600 dark:text-red-300">{createError}</p>}
        </div>

        <div className="flex items-center justify-between border-t border-ink-100 px-5 py-3 dark:border-ink-800">
          <button
            type="button"
            onClick={onBrowse}
            className="text-[12px] font-medium text-ink-500 underline-offset-2 hover:text-accent-600 hover:underline dark:text-ink-400 dark:hover:text-accent-300"
          >
            Browse notebook
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-[12px] font-medium text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
    </Layer>
  );
}
